// ══════════════════════════════════════════════════════
// PeptideVault — Unified AI Service (Items 2–6, 24–30)
// One service for all AI calls. Features never call Anthropic directly.
// ══════════════════════════════════════════════════════

// ── Credential Helpers (Item 5) ──
function hasAnthropicCredential() {
  // Electron prefers a locally encrypted key, but may safely fall back to the
  // Cloudflare Worker proxy when the local key is absent. PWA always uses Worker.
  var localKey = !!(S.settings._hasAnthropicKey || S._anthropicKeyInMemory)
  var cloudReady = !!(S.settings.cloudUrl && S.settings.cloudToken)
  if (typeof isElectron !== 'undefined' && isElectron) return localKey || cloudReady
  return cloudReady
}

function hasLocalAnthropicCredential() {
  return !!(S.settings._hasAnthropicKey || S._anthropicKeyInMemory)
}
function getAnthropicCredential() {
  if (typeof isElectron !== 'undefined' && isElectron) return S._anthropicKeyInMemory || ''
  return '' // PWA never holds the key — Worker uses ANTHROPIC_KEY secret
}
function setAnthropicCredential(key) {
  if (typeof isElectron !== 'undefined' && isElectron && window.api && window.api.saveCredential) {
    window.api.saveCredential('anthropic', key)
    S.settings.anthropicKey = ''
    S.settings._hasAnthropicKey = true
    S._anthropicKeyInMemory = key
  } else {
    // P0-2: PWA never stores the Anthropic key — Worker uses ANTHROPIC_KEY secret
    // This function should not be called on PWA, but defensively do nothing
    if (typeof showToast === 'function') showToast('PWA uses server-managed AI credentials')
    return
  }
  if (typeof saveSettings === 'function') saveSettings()
}
function deleteAnthropicCredential() {
  S.settings.anthropicKey = ''
  S.settings._hasAnthropicKey = false
  S._anthropicKeyInMemory = ''
  if (typeof isElectron !== 'undefined' && isElectron && window.api && window.api.saveCredential) window.api.saveCredential('anthropic', '')
  if (typeof saveSettings === 'function') saveSettings()
}


// P2-4: PWA AI health check — verify Worker actually has ANTHROPIC_KEY
var _pwaAIHealthCache = null
var _pwaAIHealthCacheTime = 0
async function checkPWAAIHealth() {
  var now = Date.now()
  if (_pwaAIHealthCache !== null && now - _pwaAIHealthCacheTime < 300000) return _pwaAIHealthCache // Cache 5min
  if (typeof isElectron !== 'undefined' && isElectron && hasLocalAnthropicCredential()) return true
  if (!S.settings.cloudUrl || !S.settings.cloudToken) { _pwaAIHealth = { checked:true, available:false, error:'Cloud connection not configured' }; return false }
  try {
    var healthUrl = S.settings.cloudUrl.replace('/api/data', '/api/ai-health')
    var r = await fetch(healthUrl, { headers: { 'X-Token': S.settings.cloudToken } })
    if (!r.ok) { _pwaAIHealthCache = false; _pwaAIHealthCacheTime = now; _pwaAIHealth = { checked:true, available:false, error:'AI health check failed' }; return false }
    var data = await r.json()
    _pwaAIHealthCache = !!(data.aiAvailable)
    _pwaAIHealthCacheTime = now
    _pwaAIHealth = { checked:true, available:_pwaAIHealthCache, error:_pwaAIHealthCache ? '' : 'Worker ANTHROPIC_KEY not configured' }
    return _pwaAIHealthCache
  } catch(e) { _pwaAIHealthCache = false; _pwaAIHealthCacheTime = now; _pwaAIHealth = { checked:true, available:false, error:e.message || 'AI health check failed' }; return false }
}

// ── Configuration Status (Item 7) ──
var _pwaAIHealth = { checked: false, available: false, error: '' }

async function refreshPwaAIHealth() {
  if (typeof isElectron !== 'undefined' && isElectron && hasLocalAnthropicCredential()) return { checked:true, available:true, error:'' }
  var r = await checkPWAAIHealth()
  _pwaAIHealth = { checked:true, available:!!r, error:r ? '' : (_pwaAIHealth.error || 'AI unavailable') }
  return _pwaAIHealth
}

function getAIConfigurationStatus() {
  var isElec = typeof isElectron !== 'undefined' && isElectron
  var local = isElec && hasLocalAnthropicCredential()
  var cloud = !!(S.settings.cloudUrl && S.settings.cloudToken)
  var available = local || (_pwaAIHealth.checked ? _pwaAIHealth.available : cloud)
  var label = local ? 'Configured ✓' :
    (!_pwaAIHealth.checked ? (cloud ? 'Worker AI configured' : 'Not configured') :
      (available ? 'Worker AI ready ✓' : (_pwaAIHealth.error || 'Worker AI unavailable')))
  return {
    available: available,
    platform: isElec ? 'electron' : 'pwa',
    provider: 'Anthropic',
    transport: local ? 'Electron IPC → safeStorage' : 'Cloudflare Worker Secret',
    showKeyField: isElec,
    statusLabel: label
  }
}

// ── Permission Gate (Item 26) ──
function aiPermissionFilter(contextObj) {
  var priv = S._aiPrivacy || {}
  var filtered = {}
  if (priv.peptides !== false && contextObj.peptides) filtered.peptides = contextObj.peptides
  if (priv.protocols !== false && contextObj.protocols) filtered.protocols = contextObj.protocols
  if (priv.inventory !== false && contextObj.inventory) filtered.inventory = contextObj.inventory
  if (priv.health !== false && contextObj.health) filtered.health = contextObj.health
  if (priv.labs !== false && contextObj.labs) filtered.labs = contextObj.labs
  if (priv.ice !== false && contextObj.ice) filtered.ice = contextObj.ice
  if (priv.notes !== false && contextObj.notes) filtered.notes = contextObj.notes
  if (priv.documents !== false && contextObj.documents) filtered.documents = contextObj.documents
  return filtered
}

function buildAIContextEnvelope() {
  var priv = S._aiPrivacy || {}, refs = []
  if (priv.protocols !== false) (S.protocols||[]).filter(function(p){return p.status==='active' || (S.modalData&&S.modalData.protocolId===p.id)}).slice(0,30).forEach(function(p){refs.push({ref:'[protocol:'+p.id+']',type:'protocol',id:p.id,label:p.name||p.peptideName})})
  if (priv.peptides !== false) (S.peptides||[]).filter(function(p){return p.id===S.selectedPeptide}).forEach(function(p){refs.push({ref:'[peptide:'+p.id+']',type:'peptide',id:p.id,label:p.name})})
  if (priv.labs !== false) (S.labResults||[]).slice(-5).forEach(function(l){refs.push({ref:'[lab:'+l.id+']',type:'lab',id:l.id,label:(l.panelName||'Lab')+' '+(l.date||'')})})
  return { module:S.tab||'unknown', selectedRecord:S.modalData&&S.modalData.protocolId ? {type:'protocol',id:S.modalData.protocolId} : S.selectedPeptide ? {type:'peptide',id:S.selectedPeptide} : null, sourceRefs:refs,
    citationRule:'Cite factual PeptideVault claims with the exact supplied token, such as [protocol:id]. End with a Sources line listing only tokens actually used.' }
}

function extractPeptideVaultCitations(text) { var seen={}, out=[]; String(text||'').replace(/\[(protocol|peptide|lab|inventory|document):([^\]]+)\]/g,function(all,type,id){if(!seen[all]){seen[all]=true;out.push({ref:all,type:type,id:id})}return all}); return out }

// ── AI Tool Functions (Item 25 — read-only) ──
var AI_TOOLS = {
  searchPeptides: function(query) { return (S.peptides||[]).filter(function(p) { return !p.archived && (p.name||'').toLowerCase().indexOf(query.toLowerCase()) >= 0 }).map(function(p) { return { _ref:'[peptide:'+p.id+']', id: p.id, name: p.name, skuCount: (p.skus||[]).length } }) },
  getProtocol: function(id) { var p = (S.protocols||[]).find(function(pr) { return pr.id === id }); return p ? { _ref:'[protocol:'+p.id+']', id: p.id, name: p.name, peptideName: p.peptideName, status: p.status, startDate: p.startDate, endDate: p.endDate, frequency: p.frequency, doseAmount: p.doseAmount, doseUnit: p.doseUnit, scheduleMode: p.scheduleMode } : null },
  getProtocolSchedule: function(id) { var p = (S.protocols||[]).find(function(pr) { return pr.id === id }); return p && typeof resolveProtocolSchedule === 'function' ? resolveProtocolSchedule(p).slice(0, 50) : [] },
  getInventoryForecast: function(days) { return typeof forecastInventory === 'function' ? forecastInventory(S.protocols||[], S.peptides||[], S.inventoryReservations||[], days||90).map(function(r) { return { _ref:'[inventory:'+r.peptideId+']', peptide: r.peptideName, onHand: r.onHand, available: r.available, vialsNeeded: r.vialsNeeded, shortfall: r.shortfall } }) : [] },
  getLabTrend: function(marker) { return (S.labResults||[]).map(function(p) { var m = (p.markers||[]).find(function(mk) { return mk.name === marker }); return m ? { _ref:'[lab:'+p.id+']', date: p.date, value: +m.value, unit: m.unit } : null }).filter(Boolean).slice(-50) },
  getPurchasePlan: function() { return typeof forecastInventory === 'function' && typeof purchasePlan === 'function' ? purchasePlan(forecastInventory(S.protocols||[], S.peptides||[], S.inventoryReservations||[], 90)) : [] },
  getDataHealthIssues: function() { return typeof runDataHealthChecks === 'function' ? runDataHealthChecks() : [] },
  findDocuments: function(query) { return (S.documents||[]).filter(function(d) { return (d.name||'').toLowerCase().indexOf(query.toLowerCase()) >= 0 }).map(function(d) { return { id: d.id, name: d.name, type: d.type } }).slice(0, 10) }
}

// Item 25: Anthropic tool-use definitions — model can call these during conversation
var AI_TOOL_DEFINITIONS = [
  { name: 'searchPeptides', description: 'Search peptide inventory by name', input_schema: { type: 'object', properties: { query: { type: 'string', description: 'Search query' } }, required: ['query'] } },
  { name: 'getProtocol', description: 'Get details of a specific protocol by ID', input_schema: { type: 'object', properties: { id: { type: 'string' } }, required: ['id'] } },
  { name: 'getProtocolSchedule', description: 'Get planned dose events for a protocol', input_schema: { type: 'object', properties: { id: { type: 'string' } }, required: ['id'] } },
  { name: 'getInventoryForecast', description: 'Get inventory forecast showing shortfalls', input_schema: { type: 'object', properties: { days: { type: 'number', description: 'Forecast window in days (default 90)' } } } },
  { name: 'getLabTrend', description: 'Get historical lab values for a marker', input_schema: { type: 'object', properties: { marker: { type: 'string', description: 'Lab marker name' } }, required: ['marker'] } },
  { name: 'getPurchasePlan', description: 'Get purchase recommendations based on forecast', input_schema: { type: 'object' } },
  { name: 'getDataHealthIssues', description: 'Get data quality issues', input_schema: { type: 'object' } },
  { name: 'findDocuments', description: 'Search documents by name', input_schema: { type: 'object', properties: { query: { type: 'string' } }, required: ['query'] } }
]

// Execute a tool call from AI response
function executeAITool(toolName, toolInput) {
  var fn = AI_TOOLS[toolName]
  if (!fn) return { error: 'Unknown tool: ' + toolName }
  // P0-3: Enforce AI privacy permissions on tool execution
  var priv = S._aiPrivacy || {}
  var toolPermissions = {
    searchPeptides: 'peptides', getProtocol: 'protocols', getProtocolSchedule: 'protocols',
    getInventoryForecast: 'inventory', getLabTrend: 'labs', getPurchasePlan: 'inventory',
    getDataHealthIssues: 'protocols', findDocuments: 'documents'
  }
  var requiredPerm = toolPermissions[toolName]
  if (requiredPerm && priv[requiredPerm] === false) {
    return { error: 'Access denied: ' + requiredPerm + ' data is disabled in AI privacy settings' }
  }
  try {
    var input = typeof toolInput === 'string' ? JSON.parse(toolInput) : toolInput
    return fn(input.query || input.id || input.marker || input.days || '')
  } catch (e) { return { error: e.message } }
}

// Multi-turn AI assistant with tool use
async function aiAssistantWithTools(messages, systemPrompt) {
  var allMessages = messages.slice()
  var maxTurns = 3
  for (var turn = 0; turn < maxTurns; turn++) {
    // P0-3: Filter tool definitions by privacy permissions
    var priv = S._aiPrivacy || {}
    var toolPerms = { searchPeptides:'peptides', getProtocol:'protocols', getProtocolSchedule:'protocols', getInventoryForecast:'inventory', getLabTrend:'labs', getPurchasePlan:'inventory', getDataHealthIssues:'protocols', findDocuments:'documents' }
    var permittedTools = AI_TOOL_DEFINITIONS.filter(function(t) { var p = toolPerms[t.name]; return !p || priv[p] !== false })
    var result = await aiSendRequest({ system: systemPrompt, messages: allMessages, tools: permittedTools.length > 0 ? permittedTools : null, feature: 'ai-assistant-tools' })
    if (!result.ok) return result
    // Check for tool_use blocks in raw response
    var raw = result.raw || {}
    var toolUses = (raw.content || []).filter(function(b) { return b.type === 'tool_use' })
    if (toolUses.length === 0) { result.sources = extractPeptideVaultCitations(result.content); return result } // No tool calls — final answer
    // Execute each tool call and add results
    allMessages.push({ role: 'assistant', content: raw.content })
    var toolResults = toolUses.map(function(tu) {
      var output = executeAITool(tu.name, tu.input)
      return { type: 'tool_result', tool_use_id: tu.id, content: JSON.stringify(output) }
    })
    allMessages.push({ role: 'user', content: toolResults })
  }
  return { ok: false, content: 'Max tool turns exceeded', error: 'Too many tool calls' }
}


// ── Core Send Request (Items 2-4) ──
// Normalized result: { ok, content, usage, model, error, sources }
async function aiSendRequest(opts) {
  var model = opts.model || 'claude-sonnet-4-6'
  var maxTokens = opts.maxTokens || 4096
  var messages = opts.messages || []
  var systemPrompt = opts.system || ''
  var tools = opts.tools || null
  var feature = opts.feature || 'unknown'
  var startTime = Date.now()

  // Log request metadata (Item 30 — no sensitive data)
  if (typeof debugLogEvent === 'function') debugLogEvent('info', 'AI_REQUEST', feature + ' model=' + model + ' msgs=' + messages.length)

  if (!hasAnthropicCredential()) {
    return { ok: false, content: '', usage: null, model: model, error: 'AI not configured', sources: [] }
  }

  try {
    var data
    var isElec = typeof isElectron !== 'undefined' && isElectron

    if (isElec && window.api && window.api.anthropicRequest) {
      // Electron: prefer local safeStorage key; if absent, main process proxies to
      // the same Cloudflare Worker used by the PWA (key never enters renderer).
      var body = {
        apiKey: getAnthropicCredential(),
        workerUrl: (S.settings.cloudUrl || '').replace('/api/data', '/api/ai'),
        cloudToken: S.settings.cloudToken || '',
        model: model, maxTokens: maxTokens, messages: messages
      }
      if (systemPrompt) body.system = systemPrompt
      if (tools) body.tools = tools
      var r = await window.api.anthropicRequest(body)
      if (!r.ok) throw new Error((r.data && r.data.error && r.data.error.message) || r.error || 'IPC error')
      data = r.data
      if (data && data.data && !data.content) data = data.data // unwrap Worker envelope
    } else {
      // PWA: X-Token → Worker → ANTHROPIC_KEY → Anthropic
      var body2 = { model: model, maxTokens: maxTokens, messages: messages }
      if (systemPrompt) body2.system = systemPrompt
      if (tools) body2.tools = tools
      var r2 = await fetch(S.settings.cloudUrl.replace('/api/data', '/api/ai'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Token': S.settings.cloudToken },
        body: JSON.stringify(body2)
      })
      if (!r2.ok) throw new Error('API error ' + r2.status)
      var workerResp = await r2.json()
      data = workerResp.data || workerResp // Unwrap Worker envelope
    }

    var textContent = ''
    if (data.content && Array.isArray(data.content)) {
      var textBlock = data.content.find(function(c) { return c.type === 'text' })
      if (textBlock) textContent = textBlock.text || ''
    }

    var elapsed = Date.now() - startTime
    if (typeof debugLogEvent === 'function') debugLogEvent('info', 'AI_RESPONSE', feature + ' ok=true ' + elapsed + 'ms tokens=' + (data.usage ? data.usage.output_tokens : '?'))

    return { ok: true, content: textContent, usage: data.usage || null, model: data.model || model, error: null, sources: [], raw: data }

  } catch (e) {
    if (typeof debugLogEvent === 'function') debugLogEvent('error', 'AI_ERROR', feature + ': ' + e.message)
    return { ok: false, content: '', usage: null, model: model, error: e.message, sources: [] }
  }
}

// ── Convenience Methods ──

async function aiTestConnection() {
  return aiSendRequest({ model: 'claude-haiku-4-5-20251001', maxTokens: 10, messages: [{ role: 'user', content: 'Reply OK' }], feature: 'connection-test' })
}

async function aiAnalyzeImage(base64, prompt, mediaType) {
  var supported=['image/jpeg','image/png','image/gif','image/webp']
  var type=supported.indexOf(mediaType)>=0?mediaType:'image/jpeg'
  return aiSendRequest({ messages: [{ role: 'user', content: [{ type: 'image', source: { type: 'base64', media_type: type, data: base64 } }, { type: 'text', text: prompt }] }], feature: 'image-analysis' })
}

async function aiAnalyzePDF(base64, prompt) {
  return aiSendRequest({ messages: [{ role: 'user', content: [{ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } }, { type: 'text', text: prompt }] }], feature: 'pdf-analysis' })
}

async function aiExplainCalculation(calcName, calcData) {
  var ctx = aiPermissionFilter(typeof buildAIContext === 'function' ? buildAIContext() : {})
  return aiSendRequest({
    system: 'You are a peptide and supplement research assistant. Explain PeptideVault calculations. Use the provided deterministic data as your source — do not independently recalculate. Cite specific records when possible.',
    messages: [{ role: 'user', content: 'Explain this ' + calcName + ' calculation:\n\n' + JSON.stringify(calcData) + '\n\nContext:\n' + JSON.stringify(ctx) }],
    feature: 'explain-calculation'
  })
}

async function aiSummarizeData(topic, data) {
  return aiSendRequest({
    system: 'Summarize PeptideVault data concisely. Use provided data only.',
    messages: [{ role: 'user', content: 'Summarize ' + topic + ':\n\n' + JSON.stringify(data) }],
    feature: 'summarize'
  })
}

// ── Master Plan AI Functions (Items 9-14) ──

async function aiMapCompounds(unmappedHeaders, availablePeptides, library, blends) {
  if (!hasAnthropicCredential()) return { ok: false, matches: [], error: 'AI not configured' }
  // P0-3: Check peptides permission
  var priv = S._aiPrivacy || {}
  if (priv.peptides === false) return { ok: false, matches: [], error: 'Peptide data access disabled in AI privacy settings' }
  var prompt = 'You are a peptide expert. Match these spreadsheet column headers to the closest peptide in the library.\n\n' +
    'Unmatched headers: ' + JSON.stringify(unmappedHeaders) + '\n\n' +
    'Available peptides: ' + JSON.stringify(availablePeptides.map(function(p) { return { id: p.id, name: p.name } })) + '\n\n' +
    'Library: ' + JSON.stringify((library || []).slice(0, 50).map(function(l) { return { name: l.scientificName, aliases: l.aliases } })) + '\n\n' +
    'Blends: ' + JSON.stringify((blends || []).map(function(b) { return { id: b.id, name: b.name, components: b.components } })) + '\n\n' +
    'Respond with ONLY a JSON array. Each element: {"source":"header","targetId":"id or empty","targetName":"name","confidence":0.0-1.0,"reasoning":"brief"}'
  var result = await aiSendRequest({ system: 'You are a peptide naming expert. Respond with valid JSON only, no markdown.', messages: [{ role: 'user', content: prompt }], feature: 'master-plan-mapping' })
  if (!result.ok) return { ok: false, matches: [], error: result.error }
  try {
    var parsed = JSON.parse(result.content.replace(/```json|```/g, '').trim())
    if (!Array.isArray(parsed)) return { ok: false, matches: [], error: 'Invalid AI response format' }
    // P1-2: Validate each match — required fields, types, referenced IDs
    var validPeptideIds = (S.peptides||[]).map(function(p){return p.id})
    var validated = parsed.filter(function(m) {
      if (!m.source || typeof m.source !== 'string') return false
      if (m.confidence !== undefined && (typeof m.confidence !== 'number' || m.confidence < 0 || m.confidence > 1)) return false
      // Validate targetId references a real peptide
      if (m.targetId && validPeptideIds.indexOf(m.targetId) < 0) { m.targetId = ''; m.confidence = Math.min(m.confidence || 0, 0.5); m.reasoning = (m.reasoning||'') + ' [ID not found in inventory]' }
      return true
    })
    return { ok: true, matches: validated }
  } catch (e) { return { ok: false, matches: [], error: 'AI response parse error: ' + e.message } }
}

async function aiDetectCombinations(drafts) {
  if (!hasAnthropicCredential()) return { ok: false, groups: [] }
  if ((S._aiPrivacy||{}).protocols === false) return { ok: false, groups: [] }
  var compact = drafts.map(function(d) { return { name: d.compoundName, start: d.startDate, end: d.endDate, freq: d.frequency, doses: d.totalDoses, episode: d.episodeIndex } })
  var prompt = 'These protocol episodes were parsed from one spreadsheet. Identify any that are likely one combined protocol (same schedule, complementary compounds, same start/end).\n\n' +
    JSON.stringify(compact) + '\n\nRespond with ONLY a JSON array of groups: [{"compounds":["name1","name2"],"reason":"...","confidence":0.0-1.0}]'
  var result = await aiSendRequest({ system: 'You are a peptide protocol expert. Respond with valid JSON only.', messages: [{ role: 'user', content: prompt }], feature: 'combination-detection' })
  if (!result.ok) return { ok: false, groups: [] }
  try {
      var groups = JSON.parse(result.content.replace(/\`\`\`json|\`\`\`/g, '').trim())
      // P1-2: Validate structure
      if (!Array.isArray(groups)) return { ok: false, groups: [] }
      groups = groups.filter(function(g) { return Array.isArray(g.compounds) && g.compounds.length >= 2 && typeof g.confidence === 'number' && g.confidence >= 0 && g.confidence <= 1 })
      return { ok: true, groups: groups }
    }
  catch (e) { return { ok: false, groups: [] } }
}

async function aiDetectAnomalies(drafts) {
  if (!hasAnthropicCredential()) return { ok: false, anomalies: [] }
  if ((S._aiPrivacy||{}).protocols === false) return { ok: false, anomalies: [] }
  var compact = drafts.map(function(d) {
    var doses = d.entries.slice(0, 20).map(function(e) { return e.date + (e.timeLabel?' '+e.timeLabel:'') + ':' + (e.dose.rawValue || (e.dose.numericValue + (e.dose.unit || ''))) })
    return { name: d.compoundName, freq: d.detectedFrequency.label, totalDoses: d.totalDoses, sampleDoses: doses }
  })
  var prompt = 'Review these parsed protocol episodes for anomalies: unusual dose values, unexpected schedule gaps, weekend doses on weekday-only protocols, etc.\n\n' +
    JSON.stringify(compact) + '\n\nRespond with ONLY a JSON array: [{"compound":"...","date":"YYYY-MM-DD or null","issue":"...","severity":"low|medium|high"}]'
  var result = await aiSendRequest({ system: 'You are a peptide protocol reviewer. Respond with valid JSON only.', messages: [{ role: 'user', content: prompt }], feature: 'anomaly-detection' })
  if (!result.ok) return { ok: false, anomalies: [] }
  try {
      var anomalies = JSON.parse(result.content.replace(/\`\`\`json|\`\`\`/g, '').trim())
      if (!Array.isArray(anomalies)) return { ok: false, anomalies: [] }
      var validSev = ['low','medium','high']
      anomalies = anomalies.filter(function(a) { return a.issue && typeof a.issue === 'string' && (!a.severity || validSev.indexOf(a.severity) >= 0) })
      return { ok: true, anomalies: anomalies }
    }
  catch (e) { return { ok: false, anomalies: [] } }
}

async function aiSuggestProtocolNames(drafts) {
  if (!hasAnthropicCredential()) return { ok: false, suggestions: [] }
  if ((S._aiPrivacy||{}).protocols === false) return { ok: false, suggestions: [], error: 'Protocols access disabled' }
  var compact = drafts.map(function(d) { return { raw: d.compoundName + (d.episodeIndex > 0 ? '_' + d.episodeIndex : ''), start: d.startDate, end: d.endDate, doses: d.totalDoses } })
  var prompt = 'Suggest clean, human-readable protocol names for these imported episodes.\n\n' +
    JSON.stringify(compact) + '\n\nRespond with ONLY a JSON array: [{"raw":"...","suggested":"..."}]'
  var result = await aiSendRequest({ system: 'You are a naming assistant. Respond with valid JSON only.', messages: [{ role: 'user', content: prompt }], feature: 'protocol-naming' })
  if (!result.ok) return { ok: false, suggestions: [] }
  try {
      var suggestions = JSON.parse(result.content.replace(/\`\`\`json|\`\`\`/g, '').trim())
      if (!Array.isArray(suggestions)) return { ok: false, suggestions: [] }
      suggestions = suggestions.filter(function(s) { return s.raw && s.suggested && typeof s.suggested === 'string' })
      return { ok: true, suggestions: suggestions }
    }
  catch (e) { return { ok: false, suggestions: [] } }
}


// Item 13: AI interpretation of free-form spreadsheet notes
async function aiInterpretNotes(noteTexts, compoundNames, protocolDates) {
  if (!hasAnthropicCredential() || noteTexts.length === 0) return { ok: false, interpretations: [] }
  // P1-2: Require Notes permission to send note text — check Notes independently
  if ((S._aiPrivacy||{}).notes === false) return { ok: false, interpretations: [], error: 'Notes access disabled in AI privacy settings' }
  var prompt = 'These free-form notes appear in a peptide protocol spreadsheet. Interpret each as a structured scheduling dependency or instruction.\n\n' +
    'Notes: ' + JSON.stringify(noteTexts) + '\n' +
    'Known compounds: ' + JSON.stringify(compoundNames) + '\n' +
    'Known dates: ' + JSON.stringify(protocolDates.slice(0, 20)) + '\n\n' +
    'Respond with ONLY a JSON array: [{"noteText":"...","interpretation":"...","dependencyType":"start-after|pause-during|resume-when|relative-offset|none","relatedCompound":"...or null","offsetDays":0,"confidence":0.0-1.0}]'
  var result = await aiSendRequest({ system: 'You are a protocol scheduling expert. Respond with valid JSON only.', messages: [{ role: 'user', content: prompt }], feature: 'note-interpretation' })
  if (!result.ok) return { ok: false, interpretations: [] }
  try {
      var interps = JSON.parse(result.content.replace(/\`\`\`json|\`\`\`/g, '').trim())
      if (!Array.isArray(interps)) return { ok: false, interpretations: [] }
      var validTypes = ['start-after','pause-during','resume-when','relative-offset','none']
      interps = interps.filter(function(n) {
        if (!n.noteText || typeof n.noteText !== 'string') return false
        if (!n.interpretation || typeof n.interpretation !== 'string') return false
        if (n.dependencyType && validTypes.indexOf(n.dependencyType) < 0) n.dependencyType = 'none'
        if (n.confidence !== undefined && (typeof n.confidence !== 'number' || n.confidence < 0 || n.confidence > 1)) n.confidence = 0.5
        if (n.offsetDays !== undefined && typeof n.offsetDays !== 'number') n.offsetDays = 0
        // Validate relatedCompound exists in drafts if specified
        if (n.relatedCompound) {
          var knownCompounds = (_mpiDrafts||[]).map(function(d){return d.compoundName.toLowerCase()})
          if (knownCompounds.indexOf(n.relatedCompound.toLowerCase()) < 0) n.relatedCompound = null
        }
        return true
      })
      return { ok: true, interpretations: interps }
    }
  catch (e) { return { ok: false, interpretations: [] } }
}

async function aiImportSummary(detected, drafts, mappingResults) {
  if (!hasAnthropicCredential()) return { ok: false, summary: '' }
  if ((S._aiPrivacy||{}).protocols === false) return { ok: false, summary: '', error: 'Protocols access disabled' }
  var stats = { compounds: (detected.compoundCols || []).length, episodes: drafts.length, totalDoses: drafts.reduce(function(s, d) { return s + d.totalDoses }, 0), mapped: Object.keys(mappingResults || {}).length, unmapped: drafts.filter(function(d) { return !mappingResults[d.compoundName] }).length }
  var prompt = 'Write a brief 3-4 sentence summary of this Master Plan import:\n\n' + JSON.stringify(stats)
  var result = await aiSendRequest({ system: 'Be concise. One short paragraph.', messages: [{ role: 'user', content: prompt }], feature: 'import-summary' })
  return { ok: result.ok, summary: result.content }
}

// ── Structured Output Validation (Item 29) ──
function validateAIOutput(data, requiredFields) {
  if (!data || typeof data !== 'object') return false
  for (var i = 0; i < requiredFields.length; i++) {
    if (data[requiredFields[i]] === undefined) return false
  }
  return true
}

export {
  hasAnthropicCredential, getAnthropicCredential, setAnthropicCredential, deleteAnthropicCredential,
  getAIConfigurationStatus, refreshPwaAIHealth, aiPermissionFilter, buildAIContextEnvelope, extractPeptideVaultCitations, AI_TOOLS,
  aiSendRequest, aiTestConnection, aiAnalyzeImage, aiAnalyzePDF,
  aiExplainCalculation, aiSummarizeData,
  aiMapCompounds, aiDetectCombinations, aiDetectAnomalies, aiSuggestProtocolNames, aiImportSummary,
  AI_TOOL_DEFINITIONS, executeAITool, aiAssistantWithTools,
  aiInterpretNotes,
  validateAIOutput,
  checkPWAAIHealth
}
